import { useState } from 'react';
import { Palette, Type } from 'lucide-react';
import { useSaveTheme } from './hooks.ts';
import { useAutosave } from './useAutosave.ts';
import { AutosaveStatus } from './AutosaveStatus.tsx';
import type { SiteInfo, SiteTheme } from './api.ts';
import { presetsFor, resolvePreset, SAMPLE, type Script } from './typography.ts';

const DEFAULT_PRIMARY = '#12a29b';
const DEFAULT_INK = '#0b2a33';
const HEX = /^#[0-9a-fA-F]{3,8}$/;

function ColourField({ label, value, fallback, onChange }: { label: string; value: string; fallback: string; onChange: (v: string) => void }): JSX.Element {
  const swatch = HEX.test(value) ? value : fallback;
  return (
    <div className="space-y-1.5">
      <label className="field-label">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} colour picker`}
          value={swatch}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded-md border border-input bg-background p-0.5 cursor-pointer"
        />
        <input
          type="text"
          dir="ltr"
          value={value}
          placeholder={fallback}
          onChange={(e) => onChange(e.target.value)}
          className="input font-mono text-sm w-32"
        />
      </div>
    </div>
  );
}

// One typography-preset picker (Dhivehi or English) with a live preview rendered
// in the selected faces — councils pick a designed preset, never loose fonts.
function TypePicker({
  script,
  value,
  onChange,
}: {
  script: Script;
  value: string | undefined;
  onChange: (id: string) => void;
}): JSX.Element {
  const options = presetsFor(script);
  const active = resolvePreset(script, value);
  const sample = SAMPLE[script];
  const dir = script === 'dv' ? 'rtl' : 'ltr';
  const label = script === 'dv' ? 'Dhivehi typography' : 'English typography';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label className="status-label" htmlFor={`type-${script}`}>{label}</label>
        <select
          id={`type-${script}`}
          className="input h-9 w-40 text-sm"
          value={active.id}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      <div className="rounded-lg border border-input bg-background p-4" dir={dir}>
        <div className="text-ink" style={{ fontFamily: active.heading, fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.2 }}>
          {sample.heading}
        </div>
        <div className="text-primary mt-2.5" style={{ fontFamily: active.sub, fontWeight: 600, fontSize: '.85rem', textTransform: 'uppercase', letterSpacing: '.1em' }}>
          {sample.sub}
        </div>
        <p className="text-muted-foreground mt-2" style={{ fontFamily: active.body, fontSize: '.95rem', lineHeight: 1.7 }}>
          {sample.body}
        </p>
      </div>
    </div>
  );
}

// Design tab — template, typography, council name & tagline, brand colours, logo/hero.
// The theme JSON is replaced wholesale by the API, so load the current theme and
// send the full merged object.
export function DesignPanel({ site }: { site: SiteInfo }): JSX.Element {
  const save = useSaveTheme();
  const [theme, setTheme] = useState<SiteTheme>(() => ({ ...(site.theme ?? {}) }));
  const autosave = useAutosave((t: SiteTheme) => save.mutateAsync({ theme: t }));

  const set = <K extends keyof SiteTheme>(k: K, v: string): void => {
    setTheme((prev) => {
      const next = { ...prev, [k]: v };
      autosave.schedule(next);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="font-serif text-2xl tracking-tight text-ink">Design &amp; branding</h2>
          <p className="text-sm text-muted-foreground">How your public site looks — name, colours and imagery.</p>
        </div>
        <AutosaveStatus status={autosave.status} />
      </header>

      {/* Template */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Template</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="card-paper p-4 ring-2 ring-primary flex items-start gap-3">
            <span className="mt-0.5 inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
              <Palette size={18} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-foreground">Lagoon</h4>
                <span className="pill pill-active">Selected</span>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                A modern, bilingual council template. More templates arrive in a later release.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council identity */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Council name &amp; tagline</h3>
        <div className="card-paper p-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="nameDv" className="status-label">Council name — Dhivehi</label>
              <input id="nameDv" dir="rtl" className="input thaana" value={theme.nameDv ?? ''} onChange={(e) => set('nameDv', e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="nameEn" className="status-label">Council name — English</label>
              <input id="nameEn" dir="ltr" className="input" value={theme.nameEn ?? ''} onChange={(e) => set('nameEn', e.target.value)} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="taglineDv" className="status-label">Tagline — Dhivehi</label>
              <input id="taglineDv" dir="rtl" className="input thaana" value={theme.taglineDv ?? ''} onChange={(e) => set('taglineDv', e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="taglineEn" className="status-label">Tagline — English</label>
              <input id="taglineEn" dir="ltr" className="input" value={theme.taglineEn ?? ''} onChange={(e) => set('taglineEn', e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            This name shows in your public site's masthead, hero and footer. When empty, the subdomain is used.
          </p>
        </div>
      </section>

      {/* Brand colours */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Brand colours</h3>
        <div className="card-paper p-4 flex flex-wrap gap-6">
          <ColourField label="Brand / primary" value={theme.primary ?? ''} fallback={DEFAULT_PRIMARY} onChange={(v) => set('primary', v)} />
          <ColourField label="Text / ink" value={theme.ink ?? ''} fallback={DEFAULT_INK} onChange={(v) => set('ink', v)} />
        </div>
        <p className="text-xs text-muted-foreground">
          The Lagoon template uses the brand colour for links and accents, and the ink colour for headings and body text.
        </p>
      </section>

      {/* Typography */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink flex items-center gap-2">
          <Type size={18} className="text-primary" /> Typography
        </h3>
        <div className="card-paper p-4 grid gap-5 sm:grid-cols-2">
          <TypePicker script="dv" value={theme.typePresetDv} onChange={(v) => set('typePresetDv', v)} />
          <TypePicker script="en" value={theme.typePresetEn} onChange={(v) => set('typePresetEn', v)} />
        </div>
        <p className="text-xs text-muted-foreground">
          Curated font pairings for your public site — one for Dhivehi, one for English. Each sets the heading,
          subheading and body faces together so the result always looks polished.
        </p>
      </section>

      {/* Logo + hero */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Logo &amp; hero image</h3>
        <div className="card-paper p-4 space-y-4">
          <div className="space-y-1">
            <label htmlFor="logo" className="status-label">Logo image URL</label>
            <input id="logo" type="url" dir="ltr" placeholder="https://…" className="input" value={theme.logo ?? ''} onChange={(e) => set('logo', e.target.value)} />
          </div>
          <div className="space-y-1">
            <label htmlFor="hero" className="status-label">Hero image URL</label>
            <input id="hero" type="url" dir="ltr" placeholder="https://…" className="input" value={theme.hero ?? ''} onChange={(e) => set('hero', e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground">
            Direct image uploads aren't available yet — paste an image URL (or a data: URI) for now.
          </p>
        </div>
      </section>
    </div>
  );
}
